import { Empty, Pagination, Spin, type PaginationProps } from "antd";
import { Fragment, useState, type CSSProperties, type Key, type ReactNode } from "react";
import { rowGutter } from "../../constants";

type ListPaginationPosition = "top" | "bottom" | "both";

type ListPaginationBase = PaginationProps & {
  position?: ListPaginationPosition;
};

// Server mode renders items untouched, so paging state has to come from the caller
type ListPaginationConfig =
  | (ListPaginationBase & { mode?: "client" })
  | (ListPaginationBase & {
      mode: "server";
      total: number;
      current: number;
      onChange: NonNullable<PaginationProps["onChange"]>;
    });

// Keeps room for the spinner while an empty list loads, same as antd did
const LOADING_MIN_HEIGHT = 53;

type Props<T> = {
  items: T[];
  layout?: "grid" | "column";
  columns?: number; // relevant only for grid layout
  renderItem: (item: T, index: number) => ReactNode;
  gap?: number | string;
  pagination?: false | ListPaginationConfig;
  loading?: boolean;
  split?: boolean; // relevant only for column layout
  showEmpty?: boolean;
  className?: string;
  style?: CSSProperties;
  rowKey?: (item: T, index: number) => Key;
};

export const List = <T,>({
  items,
  layout = "column",
  columns = 3,
  renderItem,
  gap = rowGutter,
  pagination = false,
  loading,
  split = false,
  showEmpty = false,
  className,
  style,
  rowKey,
}: Props<T>) => {
  const [currentPage, setCurrentPage] = useState(pagination ? (pagination.defaultCurrent ?? 1) : 1);
  const [currentPageSize, setCurrentPageSize] = useState(
    pagination ? (pagination.pageSize ?? pagination.defaultPageSize ?? 10) : 10,
  );

  const layoutStyle: CSSProperties =
    layout === "grid"
      ? {
          display: "grid",
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gap,
        }
      : {
          display: "flex",
          flexDirection: "column",
          gap,
        };

  const splitEnabled = split && layout === "column";
  // gap accepts the CSS shorthand ("row column"), but a divider only ever needs the row part
  const splitGap = typeof gap === "string" ? gap.trim().split(/\s+/)[0] : gap;

  const renderContent = (visibleItems: T[], indexOffset: number) => {
    if (!visibleItems.length) {
      if (loading) {
        return <div style={{ minHeight: LOADING_MIN_HEIGHT }} />;
      }

      if (showEmpty) {
        return (
          <div style={{ padding: "var(--ant-padding)" }}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        );
      }
    }

    return (
      <div style={layoutStyle}>
        {visibleItems.map((item, index) => {
          const itemIndex = indexOffset + index;
          const itemKey = rowKey ? rowKey(item, itemIndex) : itemIndex;
          const rendered = renderItem(item, itemIndex);

          return splitEnabled && index < visibleItems.length - 1 ? (
            <div
              key={itemKey}
              style={{ borderBlockEnd: "1px solid var(--ant-color-split)", paddingBlockEnd: splitGap }}
            >
              {rendered}
            </div>
          ) : (
            <Fragment key={itemKey}>{rendered}</Fragment>
          );
        })}
      </div>
    );
  };

  const withLoading = (content: ReactNode) =>
    loading === undefined ? content : <Spin spinning={loading}>{content}</Spin>;

  if (!pagination) {
    return (
      <div className={className} style={style}>
        {withLoading(renderContent(items, 0))}
      </div>
    );
  }

  const {
    position = "bottom",
    mode = "client",
    current,
    pageSize,
    onChange,
    onShowSizeChange,
    ...paginationProps
  } = pagination;

  const activePage = current ?? currentPage;
  const activePageSize = pageSize ?? currentPageSize;
  const total = pagination.mode === "server" ? pagination.total : items.length;
  const maxPage = Math.max(1, Math.ceil(total / activePageSize));
  const page = mode === "client" ? Math.min(activePage, maxPage) : activePage;
  const startIndex = mode === "client" ? (page - 1) * activePageSize : 0;
  const paginatedItems = mode === "client" ? items.slice(startIndex, startIndex + activePageSize) : items;

  const showTopPagination = position === "top" || position === "both";
  const showBottomPagination = position === "bottom" || position === "both";

  const handlePaginationChange: PaginationProps["onChange"] = (page, changedPageSize) => {
    if (current === undefined) {
      setCurrentPage(page);
    }

    if (pageSize === undefined) {
      setCurrentPageSize(changedPageSize);
    }

    onChange?.(page, changedPageSize);
  };

  const handleShowSizeChange: PaginationProps["onShowSizeChange"] = (page, changedPageSize) => {
    if (current === undefined) {
      setCurrentPage(page);
    }

    if (pageSize === undefined) {
      setCurrentPageSize(changedPageSize);
    }

    onShowSizeChange?.(page, changedPageSize);
  };

  const renderPagination = (paginationPosition: Exclude<ListPaginationPosition, "both">) => (
    <div
      key={paginationPosition}
      style={{
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: paginationPosition === "top" ? "var(--ant-margin-lg)" : undefined,
        marginTop: paginationPosition === "bottom" ? "var(--ant-margin-lg)" : undefined,
      }}
    >
      <Pagination
        {...paginationProps}
        current={page}
        pageSize={activePageSize}
        total={total}
        onChange={handlePaginationChange}
        onShowSizeChange={handleShowSizeChange}
      />
    </div>
  );

  return (
    <div className={className} style={style}>
      {showTopPagination && renderPagination("top")}

      {withLoading(renderContent(paginatedItems, startIndex))}

      {showBottomPagination && renderPagination("bottom")}
    </div>
  );
};
